import type { Brick } from '@models/brick'
import { Vector3 } from 'three'

/**
 * Serialization Utilities for Save/Load Functionality (Task 4.2.3)
 *
 * Provides functions to serialize and deserialize brick scenes to/from JSON format.
 *
 * Features:
 * - Serialize bricks to JSON with metadata (version, date, brick count)
 * - Deserialize JSON back to brick objects with validation
 * - Version compatibility checking
 * - Error handling for corrupted or invalid files
 */

/** Current version of the save file format */
const SAVE_FILE_VERSION = '1.0.0'

/** Serializable brick data (without Vector3 objects) */
export interface SerializedBrick {
  id: string
  type: string
  position: {
    x: number
    y: number
    z: number
  }
  rotation: number
  color: {
    r: number
    g: number
    b: number
  }
  connections: string[]
}

/** Save file format with metadata */
export interface SaveFileData {
  version: string
  metadata: {
    createdAt: string
    brickCount: number
    appVersion: string
  }
  bricks: SerializedBrick[]
}

/**
 * Serialize a brick to plain JSON-serializable format
 * Converts Vector3 objects to plain objects
 */
export function serializeBrick(brick: Brick): SerializedBrick {
  return {
    id: brick.id,
    type: brick.type,
    position: {
      x: brick.position.x,
      y: brick.position.y,
      z: brick.position.z,
    },
    rotation: brick.rotation,
    color: {
      r: brick.color.r,
      g: brick.color.g,
      b: brick.color.b,
    },
    connections: brick.connections,
  }
}

/**
 * Deserialize a plain object back to a Brick
 * Converts plain position objects back to Vector3
 */
export function deserializeBrick(data: SerializedBrick): Brick {
  return {
    id: data.id,
    type: data.type as any, // Type validation happens in validateBrickData
    position: new Vector3(data.position.x, data.position.y, data.position.z),
    rotation: data.rotation,
    color: {
      r: data.color.r,
      g: data.color.g,
      b: data.color.b,
    },
    connections: data.connections,
  }
}

/**
 * Serialize an entire scene to save file format
 */
export function serializeScene(bricks: Map<string, Brick>): SaveFileData {
  const serializedBricks = Array.from(bricks.values()).map(serializeBrick)

  return {
    version: SAVE_FILE_VERSION,
    metadata: {
      createdAt: new Date().toISOString(),
      brickCount: serializedBricks.length,
      appVersion: '1.0.0', // TODO: Get from package.json
    },
    bricks: serializedBricks,
  }
}

/**
 * Deserialize a save file back to brick map
 * Throws error if validation fails
 */
export function deserializeScene(data: SaveFileData): Map<string, Brick> {
  // Validate the save file
  validateSaveFile(data)

  // Convert bricks array to Map
  const bricksMap = new Map<string, Brick>()

  for (const serializedBrick of data.bricks) {
    const brick = deserializeBrick(serializedBrick)
    bricksMap.set(brick.id, brick)
  }

  return bricksMap
}

/**
 * Validate save file structure and data
 * Throws descriptive errors if validation fails
 */
export function validateSaveFile(data: any): asserts data is SaveFileData {
  // Check if data exists
  if (!data) {
    throw new Error('Save file is empty or invalid')
  }

  // Check version
  if (!data.version) {
    throw new Error('Save file is missing version information')
  }

  // Check version compatibility (major version must match)
  const [fileMajor] = data.version.split('.')
  const [currentMajor] = SAVE_FILE_VERSION.split('.')

  if (fileMajor !== currentMajor) {
    throw new Error(
      `Incompatible save file version: ${data.version} (expected ${SAVE_FILE_VERSION})`
    )
  }

  // Check metadata
  if (!data.metadata) {
    throw new Error('Save file is missing metadata')
  }

  // Check bricks array
  if (!Array.isArray(data.bricks)) {
    throw new Error('Save file has invalid bricks data')
  }

  // Validate each brick
  data.bricks.forEach((brick: any, index: number) => {
    try {
      validateBrickData(brick)
    } catch (error) {
      throw new Error(
        `Invalid brick at index ${index}: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  })
}

/**
 * Validate individual brick data
 */
function validateBrickData(brick: any): asserts brick is SerializedBrick {
  const requiredFields = ['id', 'type', 'position', 'rotation', 'color', 'connections']

  for (const field of requiredFields) {
    if (!(field in brick)) {
      throw new Error(`Missing required field: ${field}`)
    }
  }

  // Validate position
  if (
    typeof brick.position !== 'object' ||
    typeof brick.position.x !== 'number' ||
    typeof brick.position.y !== 'number' ||
    typeof brick.position.z !== 'number'
  ) {
    throw new Error('Invalid position data')
  }

  // Validate rotation
  if (typeof brick.rotation !== 'number') {
    throw new Error('Invalid rotation data')
  }

  // Validate color
  if (
    typeof brick.color !== 'object' ||
    typeof brick.color.r !== 'number' ||
    typeof brick.color.g !== 'number' ||
    typeof brick.color.b !== 'number'
  ) {
    throw new Error('Invalid color data')
  }

  // Validate connections
  if (!Array.isArray(brick.connections)) {
    throw new Error('Invalid connections data')
  }

  // Validate brick type (known types)
  const validTypes = ['1x1', '1x2', '2x2', '2x4', '1x4']
  if (!validTypes.includes(brick.type)) {
    throw new Error(`Invalid brick type: ${brick.type}`)
  }
}

/**
 * Generate filename for save file
 * Format: brick-builder-{timestamp}.json
 */
export function generateSaveFilename(): string {
  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .replace(/T/, '_')
    .split('.')[0] // Remove milliseconds

  return `brick-builder-${timestamp}.json`
}

/**
 * Download JSON data as a file
 */
export function downloadJSON(data: SaveFileData, filename: string): void {
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()

  // Cleanup
  URL.revokeObjectURL(url)
}

/**
 * Parse JSON file and validate
 * Returns parsed data or throws error
 */
export function parseJSONFile(fileContent: string): SaveFileData {
  try {
    const data = JSON.parse(fileContent)
    validateSaveFile(data)
    return data
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('File is not valid JSON')
    }
    throw error
  }
}
